import { useState } from "react";
import { ErrorCodes, ProblemError } from "@csa/web-foundation";
import { useT } from "../../i18n/index";
import {
  CODE_PATTERN,
  MONEY_PATTERN,
  PERCENT_PATTERN,
  type CreateConceptInput,
  type CreateCoverageInput,
} from "../../api/catalog-contracts";
import {
  Button,
  Card,
  Checkbox,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  LoadingState,
  Select,
  Table,
  TableCard,
} from "../../ui";
import { useCarriers } from "./use-catalog";
import { useCatalogLines } from "./use-catalog-mgmt";
import {
  useConcepts,
  useCoverage,
  useCreateConcept,
  useCreateCoverage,
} from "./use-product-catalog";

// Concepts + coverages management (catalog step 2, ADR 0004 §§4-5). Canonical concepts
// are per-line and list cleanly (GET /lines/:line/concepts). Coverages are the carrier
// attribute blocks a package pins; they have NO list endpoint (looked up by code) and
// create is catalog:write (NOT four-eyes). The equivalence MAPPING between a coverage
// and a concept IS four-eyes (@RequireFourEyes) — that governed propose/confirm flow is
// FLAGGED as a follow-on here, not half-built. RBAC (catalog:write) is UX.

export function ConceptCoverageManagement({ canWrite }: { canWrite: boolean }) {
  const { t } = useT();
  return (
    <section className="surf-stack" data-testid="cpt-panel">
      <div className="surf-head">
        <div>
          <h1 className="surf-title">{t("cpt.heading")}</h1>
          <p className="surf-sub muted">{t("cpt.governedNote")}</p>
        </div>
      </div>

      <ConceptsSection canWrite={canWrite} />
      <CoveragesSection canWrite={canWrite} />

      <Card className="cat-formcard">
        <div className="surf-cardhead">
          <span className="surf-cardtitle">{t("cpt.equiv.title")}</span>
          <span className="cat-append-pill">
            <Icon name="lock" size={12} strokeWidth={1.9} />
            {t("cpt.equiv.fourEyesTag")}
          </span>
        </div>
        <p className="muted surf-note" data-testid="cpt-equiv-flag">
          {t("cpt.equiv.flag")}
        </p>
      </Card>
    </section>
  );
}

// ── concepts ─────────────────────────────────────────────────────────────────

function ConceptsSection({ canWrite }: { canWrite: boolean }) {
  const { t } = useT();
  const lines = useCatalogLines();
  const [line, setLine] = useState("");
  const concepts = useConcepts(line || null);

  return (
    <>
      <TableCard
        title={t("cpt.list.title")}
        actions={
          <div className="surf-filters">
            <Select
              label={t("cat.auth.line")}
              value={line}
              onChange={(e) => setLine(e.target.value)}
            >
              <option value="">{t("cpt.pickLine")}</option>
              {(lines.data?.data ?? []).map((l) => (
                <option key={l.code} value={l.code}>
                  {l.name}
                </option>
              ))}
            </Select>
            {lines.error != null && (
              <p className="form-error" role="alert" data-testid="lines-load-failed">
                {t("cat.linesLoadFailed")}
              </p>
            )}
          </div>
        }
      >
        {line === "" && (
          <EmptyState data-testid="cpt-pickline" icon="records" title={t("cpt.pickLinePrompt")} />
        )}
        {line !== "" && concepts.isLoading && <LoadingState label={t("cpt.loading")} rows={2} />}
        {concepts.error instanceof ProblemError && (
          <ErrorState title={t("cpt.error")} description={concepts.error.problem.detail} />
        )}
        {line !== "" && concepts.data && concepts.data.data.length === 0 && (
          <EmptyState data-testid="cpt-empty" icon="records" title={t("cpt.empty")} />
        )}
        {line !== "" && concepts.data && concepts.data.data.length > 0 && (
          <Table>
            <thead>
              <tr>
                <th>{t("cat.list.code")}</th>
                <th>{t("cpt.name")}</th>
                <th>{t("cpt.definition")}</th>
              </tr>
            </thead>
            <tbody>
              {concepts.data.data.map((c) => (
                <tr key={c.code} data-testid="cpt-row">
                  <td>
                    <code>{c.code}</code>
                  </td>
                  <td className="cat-strong">{c.name}</td>
                  <td className="muted">{c.definition}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </TableCard>

      <AddConceptForm canWrite={canWrite} presetLine={line} />
    </>
  );
}

function AddConceptForm({ canWrite, presetLine }: { canWrite: boolean; presetLine: string }) {
  const { t } = useT();
  const create = useCreateConcept();
  const lines = useCatalogLines();
  const [code, setCode] = useState("");
  const [lineCode, setLineCode] = useState(presetLine);
  const [name, setName] = useState("");
  const [definition, setDefinition] = useState("");

  const codeInvalid = code.trim() !== "" && !CODE_PATTERN.test(code.trim());
  const canSubmit =
    canWrite &&
    code.trim() !== "" &&
    !codeInvalid &&
    (lineCode || presetLine) !== "" &&
    name.trim() !== "" &&
    definition.trim() !== "" &&
    !create.isPending;

  function submit() {
    const body: CreateConceptInput = {
      code: code.trim(),
      lineCode: lineCode || presetLine,
      name: name.trim(),
      definition: definition.trim(),
    };
    create.mutate(body);
  }

  const isConflict = create.error instanceof ProblemError && create.error.is(ErrorCodes.CONFLICT);

  return (
    <Card className="cat-formcard">
      <div className="surf-cardhead">
        <span className="surf-cardtitle">{t("cpt.add")}</span>
      </div>
      {!canWrite && <p className="muted surf-note">{t("cat.readOnly")}</p>}

      <form
        data-testid="cpt-create-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) submit();
        }}
      >
        <fieldset className="k-form-grid" disabled={!canWrite}>
          <Input
            label={t("cat.list.code")}
            placeholder="ayakta-tedavi"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            error={codeInvalid ? t("cat.carrier.invalidCode") : undefined}
            errorTestId="cpt-err-code"
          />
          <Select
            label={t("cat.auth.line")}
            value={lineCode || presetLine}
            onChange={(e) => setLineCode(e.target.value)}
          >
            <option value="">—</option>
            {(lines.data?.data ?? []).map((l) => (
              <option key={l.code} value={l.code}>
                {l.name}
              </option>
            ))}
          </Select>
          {lines.error != null && (
            <p className="form-error" role="alert" data-testid="lines-load-failed">
              {t("cat.linesLoadFailed")}
            </p>
          )}
          <Input label={t("cpt.name")} value={name} onChange={(e) => setName(e.target.value)} />
        </fieldset>
        <fieldset disabled={!canWrite} className="cpt-def">
          <label className="k-field">
            <span className="k-field__label">{t("cpt.definition")}</span>
            <textarea
              className="k-textarea"
              rows={3}
              value={definition}
              onChange={(e) => setDefinition(e.target.value)}
              aria-label={t("cpt.definition")}
            />
          </label>
        </fieldset>
        <div className="k-form-actions">
          <Button variant="primary" type="submit" disabled={!canSubmit} loading={create.isPending}>
            {t("cpt.submit")}
          </Button>
          {create.isSuccess && (
            <span role="status" className="form-ok">
              {t("cpt.success")}
            </span>
          )}
          {isConflict && (
            <span role="alert" className="form-error" data-testid="cpt-conflict">
              {t("cat.carrier.conflict")}
            </span>
          )}
          {create.error instanceof ProblemError && !isConflict && (
            <span role="alert" className="form-error">
              {create.error.problem.detail}
            </span>
          )}
        </div>
      </form>
    </Card>
  );
}

// ── coverages (lookup + create; equivalence mapping flagged above) ────────────

function CoveragesSection({ canWrite }: { canWrite: boolean }) {
  const { t } = useT();
  const [lookup, setLookup] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const coverage = useCoverage(lookup);

  return (
    <>
      <Card className="cat-formcard">
        <div className="surf-cardhead">
          <span className="surf-cardtitle">{t("cov.lookup.title")}</span>
        </div>
        <p className="muted surf-note">{t("cov.lookup.note")}</p>
        <form
          data-testid="cov-lookup-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (draft.trim()) setLookup(draft.trim());
          }}
        >
          <fieldset className="k-form-grid">
            <Input
              label={t("cat.list.code")}
              placeholder="allianz-ayakta-tedavi"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
          </fieldset>
          <div className="k-form-actions">
            <Button variant="secondary" type="submit" disabled={draft.trim() === ""}>
              {t("prod.lookup.submit")}
            </Button>
          </div>
        </form>

        {lookup && coverage.isLoading && <LoadingState label={t("cov.loading")} rows={2} />}
        {coverage.error instanceof ProblemError && (
          <p role="alert" className="form-error">
            {coverage.error.problem.detail}
          </p>
        )}
        {coverage.data === null && (
          <EmptyState data-testid="cov-notfound" icon="records" title={t("cov.notFound")} />
        )}
        {coverage.data && (
          <div className="cat-detail surf-stack" data-testid="cov-detail">
            <div className="cat-detail-head">
              <span className="cat-detail-eyebrow muted">{t("cov.detailEyebrow")}</span>
              <h2 className="cat-detail-name">
                {coverage.data.current.name} <code>{coverage.data.code}</code>
              </h2>
            </div>
            <div className="cat-kv">
              <div>
                <span className="muted surf-note">{t("prod.current")}</span>
                <p className="cat-strong">v{coverage.data.current.version}</p>
              </div>
              <div>
                <span className="muted surf-note">{t("cov.limit")}</span>
                <p className="rate-num">
                  {coverage.data.current.limitUnlimited
                    ? t("cov.unlimited")
                    : (coverage.data.current.limitAmount ?? "—")}
                </p>
              </div>
              <div>
                <span className="muted surf-note">{t("cov.subLimits")}</span>
                <p className="cat-strong">{coverage.data.subLimits.length}</p>
              </div>
            </div>
          </div>
        )}
      </Card>

      <AddCoverageForm canWrite={canWrite} onCreated={setLookup} />
    </>
  );
}

function AddCoverageForm({
  canWrite,
  onCreated,
}: {
  canWrite: boolean;
  onCreated: (code: string) => void;
}) {
  const { t } = useT();
  const create = useCreateCoverage();
  const carriers = useCarriers();
  const lines = useCatalogLines();
  const [code, setCode] = useState("");
  const [carrierCode, setCarrierCode] = useState("");
  const [lineCode, setLineCode] = useState("");
  const [name, setName] = useState("");
  const [limitUnlimited, setLimitUnlimited] = useState(false);
  const [limitAmount, setLimitAmount] = useState("");
  const [deductibleAmount, setDeductibleAmount] = useState("");
  const [coPayPercent, setCoPayPercent] = useState("");
  const [geographicScope, setGeographicScope] = useState("");

  const codeInvalid = code.trim() !== "" && !CODE_PATTERN.test(code.trim());
  const limitInvalid =
    !limitUnlimited && limitAmount.trim() !== "" && !MONEY_PATTERN.test(limitAmount.trim());
  const deductibleInvalid =
    deductibleAmount.trim() !== "" && !MONEY_PATTERN.test(deductibleAmount.trim());
  const coPayInvalid = coPayPercent.trim() !== "" && !PERCENT_PATTERN.test(coPayPercent.trim());
  const canSubmit =
    canWrite &&
    code.trim() !== "" &&
    !codeInvalid &&
    carrierCode !== "" &&
    lineCode !== "" &&
    name.trim() !== "" &&
    !limitInvalid &&
    !deductibleInvalid &&
    !coPayInvalid &&
    !create.isPending;

  function submit() {
    const body: CreateCoverageInput = {
      code: code.trim(),
      carrierCode,
      lineCode,
      name: name.trim(),
      limitUnlimited,
      subLimits: [],
    };
    if (!limitUnlimited && limitAmount.trim()) body.limitAmount = limitAmount.trim();
    if (deductibleAmount.trim()) body.deductibleAmount = deductibleAmount.trim();
    if (coPayPercent.trim()) body.coPayPercent = coPayPercent.trim();
    if (geographicScope.trim()) body.geographicScope = geographicScope.trim();
    create.mutate(body, { onSuccess: (res) => onCreated(res.code) });
  }

  const isConflict = create.error instanceof ProblemError && create.error.is(ErrorCodes.CONFLICT);

  return (
    <Card className="cat-formcard">
      <div className="surf-cardhead">
        <span className="surf-cardtitle">{t("cov.add")}</span>
      </div>
      {!canWrite && <p className="muted surf-note">{t("cat.readOnly")}</p>}
      <p className="muted surf-note">{t("cov.governedNote")}</p>

      <form
        data-testid="cov-create-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) submit();
        }}
      >
        <fieldset className="k-form-grid" disabled={!canWrite}>
          <Input
            label={t("cat.list.code")}
            placeholder="allianz-ayakta-tedavi"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            error={codeInvalid ? t("cat.carrier.invalidCode") : undefined}
            errorTestId="cov-err-code"
          />
          <Select
            label={t("prod.carrier")}
            value={carrierCode}
            onChange={(e) => setCarrierCode(e.target.value)}
          >
            <option value="">—</option>
            {(carriers.data?.data ?? []).map((c) => (
              <option key={c.code} value={c.code}>
                {c.current.tradeName}
              </option>
            ))}
          </Select>
          <Select
            label={t("cat.auth.line")}
            value={lineCode}
            onChange={(e) => setLineCode(e.target.value)}
          >
            <option value="">—</option>
            {(lines.data?.data ?? []).map((l) => (
              <option key={l.code} value={l.code}>
                {l.name}
              </option>
            ))}
          </Select>
          {lines.error != null && (
            <p className="form-error" role="alert" data-testid="lines-load-failed">
              {t("cat.linesLoadFailed")}
            </p>
          )}
          <Input label={t("cov.name")} value={name} onChange={(e) => setName(e.target.value)} />
          <Input
            label={t("cov.limitAmount")}
            hint={t("cov.moneyHint")}
            value={limitAmount}
            disabled={limitUnlimited}
            onChange={(e) => setLimitAmount(e.target.value)}
            error={limitInvalid ? t("cov.invalidMoney") : undefined}
          />
          <Input
            label={t("cov.deductibleAmount")}
            hint={t("cov.moneyHint")}
            value={deductibleAmount}
            onChange={(e) => setDeductibleAmount(e.target.value)}
            error={deductibleInvalid ? t("cov.invalidMoney") : undefined}
          />
          <Input
            label={t("cov.coPayPercent")}
            hint={t("cov.percentHint")}
            value={coPayPercent}
            onChange={(e) => setCoPayPercent(e.target.value)}
            error={coPayInvalid ? t("cov.invalidPercent") : undefined}
          />
          <Input
            label={t("cov.geographicScope")}
            value={geographicScope}
            onChange={(e) => setGeographicScope(e.target.value)}
          />
        </fieldset>
        <fieldset disabled={!canWrite} className="cov-unlimited">
          <Checkbox checked={limitUnlimited} onChange={(e) => setLimitUnlimited(e.target.checked)}>
            {t("cov.limitUnlimited")}
          </Checkbox>
        </fieldset>
        <div className="k-form-actions">
          <Button variant="primary" type="submit" disabled={!canSubmit} loading={create.isPending}>
            {t("cov.submit")}
          </Button>
          {create.isSuccess && (
            <span role="status" className="form-ok">
              {t("cov.success")}
            </span>
          )}
          {isConflict && (
            <span role="alert" className="form-error" data-testid="cov-conflict">
              {t("cat.carrier.conflict")}
            </span>
          )}
          {create.error instanceof ProblemError && !isConflict && (
            <span role="alert" className="form-error">
              {create.error.problem.detail}
            </span>
          )}
        </div>
      </form>
    </Card>
  );
}
